import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Check } from 'lucide-react'
import { delay } from '@/lib/motion'
import { TornPaper } from '@/components/generals/paper'
import { Ambient } from '@/components/generals/backdrops'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button-link'

interface ApplicationShellProps {
    title: string
    intro: string
    steps: string[]
    aside?: ReactNode
    children: ReactNode
}

/** Two columns: what happens next on the left, the form on a sheet of paper on the right. */
export const ApplicationShell: FC<ApplicationShellProps> = ({ title, intro, steps, aside, children }): ReactNode => {
    return (
        <section id='application' className='pt-hero relative isolate overflow-hidden pb-14 sm:pb-[90px]'>
            <Ambient field className='-z-10' />

            <Container>
                <div className='grid gap-12 lg:grid-cols-[1fr_1.25fr] lg:gap-14'>
                    <div>
                        <div className='lg:sticky lg:top-28'>
                            <h1 className='type-hero enter max-w-[14ch]' style={delay(0)}>
                                {title}
                            </h1>

                            <p className='type-lead enter mt-5 max-w-[40ch]' style={delay(100)}>
                                {intro}
                            </p>

                            <div className='enter mt-10 border-t border-ink/15 pt-7' style={delay(200)}>
                                <h2 className='text-[13px] font-medium text-ink'>
                                    What happens next
                                </h2>

                                <ol className='mt-5 flex flex-col gap-4'>
                                    {steps.map((step, index) => (
                                        <li key={step} className='flex gap-4 text-ink/80'>
                                            <span className='font-serif text-[18px] leading-[1.2] text-ink/45'>
                                                {index + 1}
                                            </span>
                                            <span>
                                                {step}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            {aside && (
                                <div className='enter mt-10' style={delay(280)}>
                                    {aside}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='enter' style={delay(160)}>
                        <TornPaper className='p-6 sm:p-9 lg:p-11'>
                            {children}
                        </TornPaper>
                    </div>
                </div>
            </Container>
        </section>
    )
}

interface FormSuccessProps {
    title: string
    body: string
    primary: { label: string, href: string }
    secondary: { label: string, href: string }
}

/** The calm confirmation that replaces a form once it has been sent. */
export const FormSuccess: FC<FormSuccessProps> = ({ title, body, primary, secondary }): ReactNode => {
    return (
        <div role='status' className='flex flex-col items-start py-4 animate-in fade-in slide-in-from-bottom-2 duration-700 ease-soft motion-reduce:animate-none sm:py-8'>
            <span className='flex size-12 items-center justify-center rounded-full bg-ink text-paper shadow-[inset_0_1px_0_rgb(255_255_255/0.2)]'>
                <Check aria-hidden='true' strokeWidth={1.75} className='size-5' />
            </span>

            <h2 className='type-section mt-7 max-w-[20ch]'>
                {title}
            </h2>

            <p className='mt-3 max-w-[44ch] text-ink/80'>
                {body}
            </p>

            <div className='mt-8 flex flex-wrap items-center gap-x-6 gap-y-4'>
                <ButtonLink href={primary.href}>
                    {primary.label}
                </ButtonLink>

                <Link href={secondary.href} className='text-[13.5px] text-ink/60 underline decoration-ink/25 underline-offset-4 transition-colors hover:text-ink'>
                    {secondary.label}
                </Link>
            </div>
        </div>
    )
}

interface HoneypotProps {
    value: string
    onChange: (value: string) => void
}

/** Visually hidden field that only bots fill in. */
export const Honeypot: FC<HoneypotProps> = ({ value, onChange }): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
            <label htmlFor='nickname'>
                Leave this field empty
            </label>
            <input
                id='nickname'
                name='nickname'
                type='text'
                tabIndex={-1}
                autoComplete='off'
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </div>
    )
}
